import { SetStateAction, useState } from "react";
import { useParams, useNavigate } from "react-router-dom";
import Layout from "../../components/Layout/Layout";
import { LAYOUT_TYPE, PATTERN, SUCCESS_MESSAGE_TIMEOUT } from "../../constants";
import {
  createApplicationVersion,
  isApplicationVersionUnique,
} from "../../api/applicationApi";
import ErrorMessage from "../../components/ErrorMessage/ErrorMessage";
import CardTextInput from "../../components/CardTextInput/CardTextInput";
import CardRadioSelect from "../../components/CardRadioSelect/CardRadioSelect";
import CardNotifications from "../../components/CardNotifications/CardNotifications";
import CardFileInput from "../../components/CardFileInput/CardFileInput";
import SuccessMessage from "../../components/SuccessMessage/SuccessMessage";

export default function CreateApplicationVersion() {
  const [version, setVersion] = useState("");
  const [platform, setPlatform] = useState("");
  const [slackWebhookUrl, setSlackWebhookUrl] = useState("");
  const [webMethod, setWebMethod] = useState("");
  const [webUrl, setWebUrl] = useState("");
  const [file, setFile] = useState<File | null>(null);
  const [validated, setValidated] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState("");
  const [success, setSuccess] = useState("");
  const params = useParams();
  const navigate = useNavigate();

  const handleChange = (event: any) => {
    const target = event.target;
    const value = target.type === "checkbox" ? target.checked : target.value;
    const name = target.name;

    if (name === "version") {
      setVersion(value);
    }
    if (name === "platform") {
      setPlatform(value);
    }
    if (name === "slackWebhookUrl") {
      setSlackWebhookUrl(value);
    }
    if (name === "webMethod") {
      setWebMethod(value);
    }
    if (name === "webUrl") {
      setWebUrl(value);
    }
  };

  const handleSubmit = (event: any) => {
    event.preventDefault();
    const form = event.currentTarget;
    if (form.checkValidity() === false || !file) {
      event.stopPropagation();
    } else {
      setSubmitting(true);
      let notifications: any = {};
      if (slackWebhookUrl) {
        notifications["slack"] = [
          {
            webhookUrl: slackWebhookUrl,
          },
        ];
      }
      if (webMethod && webUrl) {
        notifications["web"] = [
          {
            httpMethod: webMethod,
            url: webUrl,
          },
        ];
      }

      isApplicationVersionUnique(
        Number(params.applicationId),
        version,
        platform,
        (responseData: SetStateAction<any>) => {
          if (responseData.unique) {
            createApplicationVersion(
              Number(params.applicationId),
              version,
              platform,
              notifications,
              file,
              (responseData: SetStateAction<any>) => {
                setSuccess("New application version was created.");
                setError("");
                setTimeout(() => {
                  navigate(
                    "/application/" +
                      params.applicationId +
                      "/version/" +
                      responseData.id,
                    {
                      replace: true,
                    }
                  );
                }, SUCCESS_MESSAGE_TIMEOUT);
              },
              (errorText: React.SetStateAction<string>) => {
                setSubmitting(false);
                setError(errorText);
              }
            );
          } else {
            setSubmitting(false);
            setError("Application version name is not unique!");
          }
        },
        (errorText: React.SetStateAction<string>) => {
          setSubmitting(false);
          setError(errorText);
        }
      );
    }

    setValidated(true);
  };

  return (
    <>
      {error && (
        <ErrorMessage
          text={error}
          inContainer={true}
          containerClassName="form-container"
        />
      )}
      {success && (
        <SuccessMessage text={success} containerClassName="form-container" />
      )}
      <Layout
        type={LAYOUT_TYPE.FORM_PAGE}
        title="Submit Application Version"
        buttons={[
          {
            title: "Cancel",
            variant: "outline-primary",
            action: () => {
              navigate(-1);
            },
            disabled: submitting,
          },
          {
            title: "Submit",
            variant: "primary",
            typeSubmit: true,
            withSpinner: true,
            disabled: submitting,
          },
        ]}
        onFormSubmit={handleSubmit}
        validated={validated}
      >
        <CardTextInput
          title="Basic Details"
          inputs={[
            {
              name: "version",
              label: "Version",
              placeholder: "Version",
              requiredText: "Version is missing!",
              pattern: PATTERN.VERSION,
              patternViolationText: "Version is invalid!",
              value: version,
              onChange: handleChange,
            },
          ]}
          disabled={submitting}
        />
        <CardRadioSelect
          title="Select Platform"
          name="platform"
          options={[
            {
              name: "android",
              label: "Android",
              value: "ANDROID",
            },
            {
              name: "linux",
              label: "Linux",
              value: "LINUX",
            },
          ]}
          value={platform}
          onChange={handleChange}
          requiredText="Please select platform!"
          disabled={submitting}
        />
        <CardNotifications
          slackWebhookUrl={slackWebhookUrl}
          webMethod={webMethod}
          webUrl={webUrl}
          onChange={handleChange}
          disabled={submitting}
        />
        <CardFileInput
          title="Upload File"
          name="file"
          onChange={(file: File) => {
            setFile(file);
          }}
          disabled={submitting}
        />
      </Layout>
    </>
  );
}
